import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import {
  withMobileDialog,
  Icon,
  Menu,
  MenuList,
  MenuItem,
  ListItemText,
  ListSubheader,
  Checkbox,
  Tooltip,
  IconButton,
  FormControl,
  OutlinedInput,
  InputAdornment,
} from '@material-ui/core';
import Search from '@material-ui/icons/Search';
import { withRouter } from 'react-router-dom';
import { showLogsModal } from '@pingum/app-state-management/log/actions';
import { editEventTemplate } from '@pingum/app-state-management/eventTemplate/actions';

import { getTenantFromUrl } from '../../../../../utils/urlHelper';
import AuditLogsButton from '../../../../widgets/AuditLogsButton';
import styles from './styles';
import propTypes from '../../../../../utils/propTypes';
import ArchiveDeleteEventTemplateModal from '../../../../modals/ArchiveDeleteEventTemplateModal';
import RestoreEventTemplateModal from '../../../../modals/RestoreEventTemplateModal';

export const DEBOUNCE_TIME = 500;

export class EventTemplateSettingsHeaderComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      openArchiveDeleteModal: false,
      openRestoreModal: false,
      search: '',
    };
  }

  componentDidMount() {
    this.debouncedSearchChange = debounce(this.fetchData, DEBOUNCE_TIME);
  }

  handleIncludeArchivedChanged = () => {
    this.props.onPaginationUpdate(
      !this.props.includeArchived,
      this.props.pagination,
    );
  }

  handleSettingsClick = (event) => {
    this.setState({ anchorEl: event.currentTarget });
  }

  handleSettingsClose = () => {
    this.setState({ anchorEl: null });
  }

  showLogsModal = () => {
    this.props.showLogsModal({
      tenantName: getTenantFromUrl(),
      service: 'event',
      actions: ['Create Workflow', 'Update Workflow', 'Delete Workflow'],
    });
  }

  fetchData = () => {
    this.props.onPaginationUpdate(
      this.props.includeArchived,
      {
        ...this.props.pagination,
        search: this.state.search,
      },
    );
  }

  handleSearchChange = (e) => {
    this.setState({ search: e.target.value }, () => {
      this.debouncedSearchChange();
    });
  }

  handleArchiveDeleteButtonClick = () => {
    this.setState({ openArchiveDeleteModal: true });
  }

  handleCloseArchiveDeleteModal = () => {
    this.setState({ openArchiveDeleteModal: false });
  }

  handleRestoreButtonClick = () => {
    this.setState({ openRestoreModal: true });
  }

  handleCloseRestoreModal = () => {
    this.setState({ openRestoreModal: false });
  }

  render() {
    const {
      onShowInformationModal,
      history,
      fullScreen,
      selectedWorkflows,
      includeArchived,
    } = this.props;

    const {
      anchorEl,
      openArchiveDeleteModal,
      openRestoreModal,
    } = this.state;

    const deletableWorkflows = selectedWorkflows.filter((workflow) => !workflow.archived);
    const restorableWorkflows = selectedWorkflows.filter((workflow) => !!workflow.archived);

    return (
      <div style={styles.eventTemplateSettingsTitleContainer}>
        <h3 style={styles.eventTemplateSettingsTitle}>
          Workflows
        </h3>
        <div style={styles.eventTemplateSettingsActionsContainer}>
          {
            !fullScreen
              && (
                <FormControl variant="outlined">
                  <OutlinedInput
                    startAdornment={(
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    )}
                    value={this.state.search}
                    onChange={this.handleSearchChange}
                    placeholder="Search"
                    margin="dense"
                    style={styles.search}
                  />
                </FormControl>
              )
          }
          {
            !this.props.readOnly
              && [
                <Tooltip title="Add New Workflow" id="add-new-event-template-tooltip" key="add-new-event-template-tooltip">
                  <IconButton
                    disabled={this.props.loading}
                    aria-label="add"
                    onClick={() => history.push({ pathname: '/settings/workflows/new' })}
                  >
                    <Icon>add_circle</Icon>
                  </IconButton>
                </Tooltip>,
                <Tooltip title="Refresh" id="template-list-refresh-tooltip" key="template-list-refresh-tooltip">
                  <IconButton
                    disabled={this.props.loading}
                    aria-label="refresh"
                    onClick={this.fetchData}
                  >
                    <Icon>cached</Icon>
                  </IconButton>
                </Tooltip>,
                <Tooltip title="Delete or Archive" id="delete-archive-template-tooltip" key="delete-archive-template-tooltip">
                  <span> {/* wrapper needed for disabled buttons to still have a tooltip */}
                    <IconButton
                      onClick={this.handleArchiveDeleteButtonClick}
                      className="archive-actions-button"
                      disabled={deletableWorkflows.length === 0}
                    >
                      <Icon>delete</Icon>
                    </IconButton>
                  </span>
                </Tooltip>,
                includeArchived
                  ? (
                    <Tooltip title="Restore" id="restore-template-tooltip" key="restore-template-tooltip">
                      <span> {/* wrapper needed for disabled buttons to still have a tooltip */}
                        <IconButton
                          onClick={() => this.handleRestoreButtonClick()}
                          className="archive-actions-button"
                          disabled={restorableWorkflows.length === 0}
                        >
                          <Icon>restore_from_trash</Icon>
                        </IconButton>
                      </span>
                    </Tooltip>
                  )
                  : '',
                <ArchiveDeleteEventTemplateModal
                  eventTemplateIds={deletableWorkflows.map((workflow) => workflow.id)}
                  handleCloseModal={this.handleCloseArchiveDeleteModal}
                  isOpen={openArchiveDeleteModal}
                  key="delete-archive-template-modal-tooltip"
                />,
                <RestoreEventTemplateModal
                  eventTemplateIds={restorableWorkflows.map((workflow) => workflow.id)}
                  handleCloseModal={this.handleCloseRestoreModal}
                  isOpen={openRestoreModal}
                  key="restore-template-modal-tooltip"
                />,
              ]
          }
          <Tooltip title="More Actions" id="templates-more-actions-tooltip" key="templates-more-actions-tooltip">
            <IconButton
              onClick={this.handleSettingsClick}
              className="more-actions-button"
            >
              <Icon>more_vert</Icon>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={this.handleSettingsClose}
            key="templates-more-actions-menu"
          >
            <ListSubheader>
              More actions
            </ListSubheader>
            <MenuList>
              <MenuItem
                onClick={this.handleIncludeArchivedChanged}
                button
                className="include-archived-event-template-button"
                key="include-archived"
              >
                <Checkbox
                  disableRipple
                  checked={includeArchived}
                />
                <ListItemText primary="Include archived" />
              </MenuItem>
              <MenuItem
                onClick={onShowInformationModal}
                button
                className="about-event-templates-button"
                key="about"
              >
                <IconButton disableRipple>
                  <Icon>info_outline</Icon>
                </IconButton>
                <ListItemText primary="About Workflows" />
              </MenuItem>
              <MenuItem
                onClick={this.showLogsModal}
                button
                className="show-logs-button"
                key="view-audit-logs"
              >
                <AuditLogsButton />
                <ListItemText primary="View Audit Logs" />
              </MenuItem>
            </MenuList>
          </Menu>
        </div>
      </div>
    );
  }
}

EventTemplateSettingsHeaderComponent.propTypes = {
  onShowInformationModal: PropTypes.func,
  includeArchived: PropTypes.bool,
  showLogsModal: PropTypes.func,
  onPaginationUpdate: PropTypes.func,
  pagination: PropTypes.shape(propTypes.pagination),
  readOnly: PropTypes.bool,
  loading: PropTypes.bool,
  fullScreen: PropTypes.bool,
  selectedWorkflows: PropTypes.array,
  history: PropTypes.object,
};

EventTemplateSettingsHeaderComponent.defaultProps = {
  onShowInformationModal: () => {},
  includeArchived: false,
  showLogsModal: () => {},
  onPaginationUpdate: () => {},
  pagination: {
    page: 0,
    size: 10,
    search: '',
    sort: ['name,desc'],
  },
  readOnly: false,
  loading: false,
  fullScreen: true,
  selectedWorkflows: [],
  history: { push: () => {} },
};

export const mapStateToProps = (state) => ({ includeArchived: state.eventTemplate.includeArchived });

export const mapDispatchToProps = (dispatch) => ({
  showLogsModal: (request) => dispatch(showLogsModal(request)),
  editEventTemplate: (eventTemplateId, request) => dispatch(editEventTemplate(eventTemplateId, request)),
});

export default withRouter(connect(
  null,
  mapDispatchToProps,
)(withMobileDialog({ breakpoint: 'xs' })(EventTemplateSettingsHeaderComponent)));
